import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  MoreHorizontal,
  Edit,
  Trash2,
  Play,
  Pause,
  History,
  RefreshCw,
  Filter,
  Search
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/auth';
import EmailTriggerService, { EmailTriggerRule } from '@/api/emailTriggers';
import {
  TRIGGER_TYPE_LABELS,
  TriggerType
} from '@/types/sms-triggers';
import { toast } from '@/hooks/use-toast';
import { formatTimeDelaySimple } from '@/utils/sms/time-formatting';

interface EmailTriggerListProps {
  triggers: EmailTriggerRule[];
  loading: boolean;
  onEdit: (triggerId: string) => void;
  onRefresh: () => void;
}

const EmailTriggerList: React.FC<EmailTriggerListProps> = ({
  triggers,
  loading,
  onEdit,
  onRefresh
}) => {
  const { user } = useAuth();
  const [actionLoading, setActionLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [triggerToDelete, setTriggerToDelete] = useState<EmailTriggerRule | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyTriggerId, setHistoryTriggerId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Map database trigger types to display labels
  const getTriggerTypeLabel = (dbType: string): string => {
    switch (dbType) {
      case 'customer_activity_update':
      case 'order_confirmed':
      case 'nth_order':
      case 'first_order':
      case 'spending_milestone':
        return 'Customer Milestone';
      case 'customer_inactive':
        return 'Re-engagement';
      default:
        return dbType;
    }
  };

  // Map frontend trigger types to database trigger types
  const mapFrontendToDbType = (frontendType: string): string[] => {
    switch (frontendType) {
      case 'milestone':
        // Database values for milestone triggers after unification
        return ['customer_activity_update', 'order_confirmed', 'nth_order', 'first_order', 'spending_milestone'];
      case 're_engagement':
        return ['customer_inactive'];
      default:
        return [frontendType];
    }
  };

  // Filter triggers based on search and filters
  const filteredTriggers = triggers.filter(trigger => {
    const matchesSearch = !searchTerm ||
      trigger.rule_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trigger.rule_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trigger.email_subject?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && trigger.is_active) ||
      (statusFilter === 'paused' && !trigger.is_active);

    const matchesType = typeFilter === 'all' ||
      mapFrontendToDbType(typeFilter).includes(trigger.trigger_type);

    return matchesSearch && matchesStatus && matchesType;
  });

  const handleToggleTrigger = async (trigger: EmailTriggerRule) => {
    if (!user?.organisationId) return;

    setActionLoading(true);
    try {
      await EmailTriggerService.toggleTrigger(
        trigger.id,
        !trigger.is_active,
        user.organisationId
      );

      toast({
        title: trigger.is_active ? 'Trigger Paused' : 'Trigger Activated',
        description: `${trigger.rule_name} has been ${trigger.is_active ? 'paused' : 'activated'}`,
      });

      onRefresh();
    } catch (error: any) {
      console.error('Error toggling trigger:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to toggle trigger',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClick = (trigger: EmailTriggerRule) => {
    setTriggerToDelete(trigger);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!triggerToDelete || !user?.organisationId) return;

    setActionLoading(true);
    try {
      await EmailTriggerService.deleteTrigger(
        triggerToDelete.id,
        user.organisationId
      );

      toast({
        title: 'Trigger Deleted',
        description: `${triggerToDelete.rule_name} has been deleted successfully`,
      });

      setDeleteDialogOpen(false);
      setTriggerToDelete(null);
      onRefresh();
    } catch (error: any) {
      console.error('Error deleting trigger:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete trigger',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewHistory = (triggerId: string) => {
    // TODO: Phase 9a - EmailTriggerExecutionHistory
    toast({
      title: 'Coming Soon',
      description: 'Execution history will be available in Phase 9a',
    });
  };

  const getStatusBadge = (trigger: EmailTriggerRule) => {
    if (!trigger.is_active) {
      return <Badge variant="secondary">Paused</Badge>;
    }

    // Check if expired
    if (trigger.expires_at && new Date(trigger.expires_at) < new Date()) {
      return <Badge variant="destructive">Expired</Badge>;
    }

    // Check if not yet started
    if (trigger.starts_at && new Date(trigger.starts_at) > new Date()) {
      return <Badge variant="outline">Scheduled</Badge>;
    }

    return <Badge variant="default">Active</Badge>;
  };


  const truncateMessage = (message: string, maxLength: number = 60) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="w-32">
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="w-32">
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="h-4 bg-gray-200 rounded flex-1 animate-pulse" />
                  <div className="h-4 bg-gray-200 rounded w-20 animate-pulse" />
                  <div className="h-4 bg-gray-200 rounded w-16 animate-pulse" />
                  <div className="h-8 bg-gray-200 rounded w-8 animate-pulse" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Filters and Search */}
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search triggers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
            </SelectContent>
          </Select>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {Object.entries(TRIGGER_TYPE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Triggers Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Triggers ({filteredTriggers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredTriggers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                  ? 'No triggers match your current filters.'
                  : 'No triggers have been created yet.'
                }
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Timing</TableHead>
                    <TableHead>Email Preview</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTriggers.map((trigger) => (
                    <TableRow key={trigger.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{trigger.rule_name}</div>
                          {trigger.rule_description && (
                            <div className="text-sm text-muted-foreground">
                              {trigger.rule_description}
                            </div>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <Badge variant="outline">
                          {getTriggerTypeLabel(trigger.trigger_type)}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        {getStatusBadge(trigger)}
                      </TableCell>

                      <TableCell>
                        <div className="text-sm">
                          {formatTimeDelaySimple(trigger.time_delay)}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="space-y-1">
                          <div className="text-sm font-medium truncate max-w-xs">
                            {trigger.email_subject}
                          </div>
                          <div className="text-xs text-muted-foreground truncate max-w-xs">
                            {truncateMessage(trigger.email_content_text || 'No preview available')}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="text-sm text-muted-foreground">
                          {new Date(trigger.created_at || '').toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </div>
                      </TableCell>

                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onEdit(trigger.id)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>

                            <DropdownMenuItem
                              onClick={() => handleToggleTrigger(trigger)}
                              disabled={actionLoading}
                            >
                              {trigger.is_active ? (
                                <>
                                  <Pause className="mr-2 h-4 w-4" />
                                  Pause
                                </>
                              ) : (
                                <>
                                  <Play className="mr-2 h-4 w-4" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>

                            <DropdownMenuItem onClick={() => handleViewHistory(trigger.id)}>
                              <History className="mr-2 h-4 w-4" />
                              View History
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />

                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(trigger)}
                              disabled={actionLoading}
                              className="text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Trigger</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{triggerToDelete?.rule_name}"?
              This action cannot be undone and will stop all future automated emails for this trigger.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History Dialog - TODO: Phase 9a */}
      {/* Will be enabled when EmailTriggerExecutionHistory is ready */}
    </>
  );
};

export default EmailTriggerList;
